import vmClient from 'app/services/vmClient';
import { Sentence, ISentence } from 'app/constants/schemas/Sentence';
import { Offset, OffsetMarshaller } from 'app/constants/schemas/Offset';
import { Entity, IEntityType, IEntity, EntityStringId } from 'app/types/entity';
import { DateMarshaller } from 'app/constants/schemas/Date';
import { TranslationsForEntity, ALL_TRANSLATIONS } from 'app/constants/schemas/translations';

export interface IWordSentence extends IEntity {
  attributes: {
    id: EntityStringId;
    exemplar: boolean;
    wordOffsets: Offset[];
    /* This Offset[] | null actually encodes a more complex relationship than might be immediately apparent.
     * If the transliterationOffsets are `null`, it means that no offsets should ever be set for
     * the word sentence. If the word/sentence language do not prefer transliterations, then this
     * would be set to null. Conversely, if the word/sentence do prefer transliteration, this should
     * never be null. The absence of a transliteration in that situation would be represented by
     * an empty list.
     */
    transliterationOffsets: Offset[] | null;
    createdAt: Date;
    updatedAt: Date;
  };
  associations: {
    sentence: ISentence;
  };
}

export type WordSentence = Entity<IWordSentence>;
// defineClass is a class factory, normally classes create a runtime variable and identifier in the type namespace.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export const WordSentence: IEntityType<IWordSentence> = vmClient.defineClass({
  typeName: 'WordSentence',
  version: 2,
  lockable: true,
  persisted: true,
  attributes: {
    exemplar: 'boolean',
    wordOffsets: {
      collection: true,
      type: Offset,
      marshaller: OffsetMarshaller,
      notNull: true,
    },
    transliterationOffsets: {
      collection: true,
      type: Offset,
      marshaller: OffsetMarshaller,
    },
    createdAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
    updatedAt: {
      type: Date,
      marshaller: DateMarshaller,
      notNull: true,
    },
  },
  associations: {
    sentence: {
      type: Sentence,
      shared: true,
    },
  },
});

export const WordSentenceFieldTranslations: Partial<TranslationsForEntity<IWordSentence>> = {
  wordOffsets: {
    getLabel: (t) => t('entity-fields.WordSentence.wordOffsets.label'),
  },
  transliterationOffsets: {
    getLabel: (t) => t('entity-fields.WordSentence.transliterationOffsets.label'),
  },
  exemplar: {
    getLabel: (t) => t('entity-fields.WordSentence.exemplar.label'),
    getInlineLabel: (t) => t('entity-fields.WordSentence.exemplar.inline-label'),
  },
};

ALL_TRANSLATIONS.set(WordSentence, WordSentenceFieldTranslations);
